import { useCallback, useRef, useState } from "react"
import type { MutableRefObject } from "react"

import { format } from "date-fns"
import { toast } from "sonner"

import {
  useBulkCreate,
  useBulkUpdateStopInstances,
  useCreateRouteInstance,
  useDeleteStopInstance,
  useSavePlannedRoute,
  useUpdateRouteInstance,
} from "@/hooks/useCollections"
import { formatDayLong } from "@/lib/dateFormats"
import {
  cloneRouteDates,
  describeRouteChanges,
  findRouteInstanceOn,
  formatTimeForDb,
  futureExcludedDates,
  plannedStopsPayload,
  recurringDaysForSave,
  routeEqualsOriginal,
} from "@/lib/plannedRouteMapping"
import type { Location, PlannedRoute, RouteInstance, StopInstance } from "@/types/collections"

import type { InstanceEditMode } from "./types"

export interface SaveConfirmPending {
  routes: PlannedRoute[]
  summary: { routeCode: string; changes: string[] }[]
}

/** Saves ask first: this day only (route instance) or all future routes (template, cascaded to instances). */
export function usePlannedRoutePersistence({
  routes,
  setRoutes,
  originalRoutes,
  setOriginalRoutes,
  setHasUnsavedChanges,
  initialTemplateRoutesRef,
  instanceEditMode,
  setInstanceEditMode,
  selectedDate,
  routeInstances,
  getLocationById,
  refetchRoutes,
  refetchInstances,
  resetStopDrag,
}: {
  routes: PlannedRoute[]
  setRoutes: (routes: PlannedRoute[]) => void
  originalRoutes: PlannedRoute[]
  setOriginalRoutes: (routes: PlannedRoute[]) => void
  setHasUnsavedChanges: (value: boolean) => void
  initialTemplateRoutesRef: MutableRefObject<PlannedRoute[]>
  instanceEditMode: InstanceEditMode
  setInstanceEditMode: (mode: InstanceEditMode) => void
  selectedDate: Date
  routeInstances: RouteInstance[] | undefined
  getLocationById: (locationId: string) => Location | undefined
  refetchRoutes: () => Promise<unknown>
  refetchInstances: () => Promise<unknown>
  resetStopDrag: () => void
}) {
  const [isSaving, setIsSaving] = useState(false)
  const [saveConfirmPending, setSaveConfirmPending] = useState<SaveConfirmPending | null>(null)
  const skipRecurringConfirmRef = useRef(false)
  /** When set, these route ids save as this-day-only instances without the dialog. */
  const pendingInstanceOnlyRouteIdsRef = useRef<Set<string> | null>(null)

  // save_planned_route does create/update, stops and cascade in one call.
  const savePlannedRoute = useSavePlannedRoute()
  const createRouteInstance = useCreateRouteInstance()
  const updateRouteInstance = useUpdateRouteInstance()
  const deleteStopInstance = useDeleteStopInstance()
  const bulkCreate = useBulkCreate()
  const bulkUpdateStopInstances = useBulkUpdateStopInstances()

  const saveInstanceChanges = useCallback(
    async (route: PlannedRoute) => {
      const dateStr = format(selectedDate, "yyyy-MM-dd")

      const existingInstance = findRouteInstanceOn(routeInstances, route.id, selectedDate)

      let instanceId: string

      if (existingInstance) {
        await updateRouteInstance.mutateAsync({
          id: existingInstance.id,
          data: {
            planned_start_time: route.plannedStartTime.toISOString(),
            planned_end_time: route.plannedEndTime.toISOString(),
            is_modified: true,
            modification_notes: "Modified via planning timeline",
            modified_at: new Date().toISOString(),
          },
        })
        instanceId = existingInstance.id

        // Stop instances match by sequence; two-phase when reordering.
        const existingStops = ((existingInstance.stops as StopInstance[] | undefined) || [])
          .slice()
          .sort((a, b) => a.sequence - b.sequence)

        if (existingStops.length > 0 && route.stops.length > 0) {
          const tempSequenceBase = 10000
          const tempUpdates = existingStops.map((s, i) => ({
            id: s.id,
            data: { sequence: tempSequenceBase + i } as Record<string, unknown>,
          }))
          await bulkUpdateStopInstances.mutateAsync(tempUpdates)
        }

        const stopUpdates: { id: string; data: Record<string, unknown> }[] = []
        const toCreate: { entity: "stop_instances"; data: Record<string, unknown> }[] = []

        for (let i = 0; i < route.stops.length; i++) {
          const stop = route.stops[i]
          const location = getLocationById(stop.locationId)
          const existingStop = existingStops[i]
          if (existingStop) {
            stopUpdates.push({
              id: existingStop.id,
              data: {
                sequence: i,
                planned_arrival: stop.plannedArrival.toISOString(),
                planned_departure: stop.plannedDeparture.toISOString(),
                location_id: stop.locationId,
                name: stop.name || location?.site_name || "",
                address: stop.address || location?.address || "",
                postcode: stop.postcode || location?.postcode || "",
                stop_type: stop.slotType || "shipper_collection",
                slot_services: stop.slotServices || [],
                slot_pnl: stop.slotPnl || [],
                service_time_minutes: stop.serviceTimeMinutes ?? 30,
                haulage_delivery_revenue_gbp:
                  stop.slotType === "haulage_delivery"
                    ? (stop.haulageDeliveryRevenueGbp ?? null)
                    : null,
                direct_injection_cost_gbp:
                  stop.slotType === "direct_injection"
                    ? (stop.directInjectionCostGbp ?? null)
                    : null,
              },
            })
          } else {
            toCreate.push({
              entity: "stop_instances",
              data: {
                route_instance_id: instanceId,
                location_id: stop.locationId,
                sequence: i,
                name: stop.name || location?.site_name || "",
                address: stop.address || location?.address || "",
                postcode: stop.postcode || location?.postcode || "",
                lat: stop.lat ?? location?.latitude ?? 0,
                lng: stop.lng ?? location?.longitude ?? 0,
                stop_type: stop.slotType || "shipper_collection",
                slot_services: stop.slotServices || [],
                slot_pnl: stop.slotPnl || [],
                planned_arrival: stop.plannedArrival.toISOString(),
                planned_departure: stop.plannedDeparture.toISOString(),
                service_time_minutes: stop.serviceTimeMinutes ?? 30,
                status: "scheduled",
                delay_minutes: 0,
                volume_forecast: stop.volumeEstimated ?? 0,
                loading_units_forecast: stop.loadingUnitsEstimated ?? 0,
                is_adhoc: false,
                is_skipped: false,
                haulage_delivery_revenue_gbp:
                  stop.slotType === "haulage_delivery"
                    ? (stop.haulageDeliveryRevenueGbp ?? null)
                    : null,
                direct_injection_cost_gbp:
                  stop.slotType === "direct_injection"
                    ? (stop.directInjectionCostGbp ?? null)
                    : null,
              },
            })
          }
        }

        if (stopUpdates.length > 0) {
          await bulkUpdateStopInstances.mutateAsync(stopUpdates)
        }
        if (toCreate.length > 0) {
          await bulkCreate.mutateAsync(toCreate)
        }
        for (let i = route.stops.length; i < existingStops.length; i++) {
          await deleteStopInstance.mutateAsync(existingStops[i].id)
        }
      } else {
        const instanceResult = await createRouteInstance.mutateAsync({
          planned_route_id: route.id,
          code: `${route.code}-${dateStr.replace(/-/g, "")}`,
          date: dateStr,
          planned_start_time: route.plannedStartTime.toISOString(),
          planned_end_time: route.plannedEndTime.toISOString(),
          depot: route.depot,
          operator: route.operator || "HIVED",
          estimated_cost: route.estimatedCost,
          status: "scheduled",
          is_adhoc: false,
          is_locked: true,
          is_modified: true,
          modification_notes: "Created as modified instance via planning timeline",
          total_stops: route.stops.length,
          completed_stops: 0,
          delay_minutes: 0,
        })

        instanceId = instanceResult?.id

        if (!instanceId) {
          throw new Error("Failed to create route instance")
        }

        const stopInstancesToCreate = route.stops.map((stop) => {
          const location = getLocationById(stop.locationId)

          return {
            entity: "stop_instances" as const,
            data: {
              route_instance_id: instanceId,
              location_id: stop.locationId,
              sequence: stop.sequence,
              name: stop.name || location?.site_name || "",
              address: stop.address || location?.address || "",
              postcode: stop.postcode || location?.postcode || "",
              lat: stop.lat || location?.latitude || 0,
              lng: stop.lng || location?.longitude || 0,
              stop_type: stop.slotType || "shipper_collection",
              slot_services: stop.slotServices || [],
              slot_pnl: stop.slotPnl || [],
              planned_arrival: stop.plannedArrival.toISOString(),
              planned_departure: stop.plannedDeparture.toISOString(),
              service_time_minutes: stop.serviceTimeMinutes || 30,
              status: "scheduled",
              delay_minutes: 0,
              volume_forecast: stop.volumeEstimated || 0,
              loading_units_forecast: stop.loadingUnitsEstimated || 0,
              is_adhoc: false,
              is_skipped: false,
            },
          }
        })

        if (stopInstancesToCreate.length > 0) {
          await bulkCreate.mutateAsync(stopInstancesToCreate)
        }
      }

      return instanceId
    },
    [
      selectedDate,
      routeInstances,
      createRouteInstance,
      updateRouteInstance,
      bulkCreate,
      bulkUpdateStopInstances,
      deleteStopInstance,
      getLocationById,
    ],
  )

  const handleSaveChanges = useCallback(async () => {
    if (isSaving) return

    setIsSaving(true)
    try {
      const isTempId = (id: string | undefined) => !id || id.startsWith("temp-")

      const isInstanceMode = instanceEditMode !== null
      const instanceOnlyIds = pendingInstanceOnlyRouteIdsRef.current

      // Any change to an existing route always asks this day vs all future.
      const routesWithChanges = routes.filter((route) => {
        const original = originalRoutes.find((r) => r.id === route.id)
        return !isTempId(route.id) && original && !routeEqualsOriginal(route, original)
      })
      const scopeAlreadyChosen =
        skipRecurringConfirmRef.current || (instanceOnlyIds != null && instanceOnlyIds.size > 0)
      if (routesWithChanges.length > 0 && !scopeAlreadyChosen) {
        const summary = routesWithChanges.map((route) => {
          const original = originalRoutes.find((r) => r.id === route.id)
          return { routeCode: route.code, changes: describeRouteChanges(route, original) }
        })
        setSaveConfirmPending({ routes: routesWithChanges, summary })
        setIsSaving(false)
        return
      }

      for (const route of routes) {
        const original = originalRoutes.find((r) => r.id === route.id)

        if (instanceOnlyIds?.has(route.id)) {
          await saveInstanceChanges(route)
          continue
        }

        // The route opened as an instance saves as instance only, unless "all future routes" was chosen.
        if (
          isInstanceMode &&
          instanceEditMode?.routeId === route.id &&
          !skipRecurringConfirmRef.current
        ) {
          await saveInstanceChanges(route)
          continue
        }

        // Skip unchanged routes before the instance check, or every route with an instance would save.
        if (!isTempId(route.id) && original && routeEqualsOriginal(route, original)) {
          continue
        }

        const hasInstanceForSelectedDate = !!findRouteInstanceOn(
          routeInstances,
          route.id,
          selectedDate,
        )
        if (hasInstanceForSelectedDate && !skipRecurringConfirmRef.current) {
          await saveInstanceChanges(route)
          continue
        }

        const routeDays = recurringDaysForSave(route)
        const stopsPayload = plannedStopsPayload(route)

        if (isTempId(route.id)) {
          await savePlannedRoute.mutateAsync({
            route: {
              code: route.code,
              name: route.code,
              category: route.category || "Other",
              days: routeDays,
              default_start_time: formatTimeForDb(route.plannedStartTime),
              default_end_time: formatTimeForDb(route.plannedEndTime),
              depot: route.depot,
              operator: route.operator || "HIVED",
              vehicle_type: route.vehicleType,
              estimated_cost: route.estimatedCost,
              status: route.status,
              effective_from: format(route.date, "yyyy-MM-dd"),
              effective_to: route.recurringEndDate
                ? format(route.recurringEndDate, "yyyy-MM-dd")
                : undefined,
              excluded_dates: futureExcludedDates(route.excludedDates),
            },
            stops: stopsPayload,
          })
        } else {
          const saveResult = await savePlannedRoute.mutateAsync({
            routeId: route.id,
            route: {
              code: route.code,
              name: route.code,
              category: route.category || "Other",
              days: routeDays,
              default_start_time: formatTimeForDb(route.plannedStartTime),
              default_end_time: formatTimeForDb(route.plannedEndTime),
              depot: route.depot,
              operator: route.operator || "HIVED",
              vehicle_type: route.vehicleType,
              estimated_cost: route.estimatedCost,
              status: route.status,
              effective_from: format(
                initialTemplateRoutesRef.current?.find((r) => r.id === route.id)?.date ??
                  route.date,
                "yyyy-MM-dd",
              ),
              effective_to: route.recurringEndDate
                ? format(route.recurringEndDate, "yyyy-MM-dd")
                : undefined,
              excluded_dates: futureExcludedDates(route.excludedDates),
            },
            stops: stopsPayload,
            cascade: true,
            fromDate: format(selectedDate, "yyyy-MM-dd"),
          })
          if (saveResult?.cascadeError) {
            toast.error("Route saved, but future instances could not be updated", {
              description: saveResult.cascadeError,
            })
          }
        }
      }

      skipRecurringConfirmRef.current = false
      pendingInstanceOnlyRouteIdsRef.current = null
      setSaveConfirmPending(null)

      setHasUnsavedChanges(false)
      setOriginalRoutes([])

      if (isInstanceMode) {
        setInstanceEditMode(null)
      }

      // Instances before routes, or the routes effect renders template times against stale instances.
      await refetchInstances()
      await refetchRoutes()

      toast.success(isInstanceMode ? "Instance saved" : "Changes saved", {
        description: isInstanceMode
          ? `Route instance for ${formatDayLong(selectedDate)} has been saved.`
          : "All route planning changes have been saved.",
      })
    } catch (error) {
      console.error("Error saving changes:", error)
      toast.error("Error saving changes", {
        description:
          error instanceof Error ? error.message : "Failed to save changes. Please try again.",
      })
    } finally {
      setIsSaving(false)
    }
  }, [
    routes,
    originalRoutes,
    savePlannedRoute,
    refetchRoutes,
    refetchInstances,
    isSaving,
    instanceEditMode,
    saveInstanceChanges,
    selectedDate,
    routeInstances,
    initialTemplateRoutesRef,
    setHasUnsavedChanges,
    setOriginalRoutes,
    setInstanceEditMode,
  ])

  const handleDiscardChanges = useCallback(() => {
    if (originalRoutes.length > 0) {
      const restoredRoutes = originalRoutes.map(cloneRouteDates)
      setRoutes(restoredRoutes)
    }
    setHasUnsavedChanges(false)
    setOriginalRoutes([])
    resetStopDrag()
  }, [originalRoutes, setRoutes, setHasUnsavedChanges, setOriginalRoutes, resetStopDrag])

  const applyToThisDayOnly = () => {
    if (saveConfirmPending?.routes.length) {
      pendingInstanceOnlyRouteIdsRef.current = new Set(saveConfirmPending.routes.map((r) => r.id))
      setSaveConfirmPending(null)
      handleSaveChanges()
    }
  }

  const applyToAllFuture = () => {
    skipRecurringConfirmRef.current = true
    setSaveConfirmPending(null)
    handleSaveChanges()
  }

  return {
    isSaving,
    setIsSaving,
    saveConfirmPending,
    setSaveConfirmPending,
    handleSaveChanges,
    handleDiscardChanges,
    applyToThisDayOnly,
    applyToAllFuture,
  }
}
